import { FALLBACK_RUNTIMES, runSourceCapabilities, type FallbackRuntime, type NodeDTO, type NodeRuntimeStatus, type Runtime, type RunSourceCapabilities } from "@roomate/core";
import { RUNTIME_LABEL } from "./run-runtime.ts";

/** The four seat runtimes a node can report (bring-your-own-seats §3.2), in picker order. */
export const SEAT_RUNTIMES = ["claude-code", "codex", "cursor", "antigravity"] as const;
export type SeatRuntime = (typeof SEAT_RUNTIMES)[number];

export type SeatState = "ready" | "connect" | "needs_keychain" | "unsupported" | "not_installed" | "unknown";

export const SEAT_STATE_LABEL: Record<SeatState, string> = {
  ready: "Ready",
  connect: "Connect",
  needs_keychain: "Needs Keychain",
  unsupported: "Not connectable",
  not_installed: "Not installed",
  unknown: "Not reported",
};

export function isSeatRuntime(runtime: Runtime | null): runtime is SeatRuntime {
  return runtime !== null && (SEAT_RUNTIMES as readonly string[]).includes(runtime);
}

/** One runtime's chip state, from what the node reported. */
export function seatState(status: NodeRuntimeStatus | undefined): SeatState {
  if (!status) return "unknown";
  if (status.ready) return "ready";
  if (!status.installed) return "not_installed";
  if (status.connectable === "needs_keychain") return "needs_keychain";
  if (status.connectable === "no_config_dir") return "unsupported";
  return "connect";
}

/** The next command for the person to run on the node. Null when nothing is left to do. */
export function nextSeatCommand(runtime: SeatRuntime, state: SeatState): string | null {
  switch (state) {
    case "ready":
    case "unknown":
    case "unsupported":
      return null;
    case "not_installed":
      return runtime === "claude-code" ? "Install Claude Code, then run claude auth login --claudeai" : `Install ${RUNTIME_LABEL[runtime]}, then run roomate runtime connect ${runtime} --agent <name>`;
    case "needs_keychain":
      return `roomate runtime status`;
    case "connect":
      // The node CLI only connects the non-Claude runtimes, and needs an agent this node holds a token for (runtime-connect.ts).
      return runtime === "claude-code" ? "claude auth login --claudeai" : `roomate runtime connect ${runtime} --agent <name>`;
  }
}

export function seatStatus(node: Pick<NodeDTO, "runtimes">, runtime: SeatRuntime): NodeRuntimeStatus | undefined {
  return node.runtimes?.find((r) => r.runtime === runtime);
}

/** Nodes that hold this agent and have reported their runtimes (an older node reports none: "unknown"). */
function reportingNodes(nodes: NodeDTO[], agentId: string): NodeDTO[] {
  return nodes.filter((n) => n.runtimes !== null && n.agentIds.includes(agentId));
}

/** A node silent for longer than this reports "unknown", not "not ready". Mirrors RUNTIME_REPORT_FRESH_MS in the hub's NodeRunSource (apps/server/src/runtime/node-run-source.ts). */
export const RUNTIME_REPORT_FRESH_MS = 30 * 60_000;

export type Availability = { state: SeatState; node: string | null; blocker: string | null };

export type AvailabilityOptions = {
  /** The agent runs Claude Code on an API key: only a missing CLI blocks it, never the subscription login (hub: keyAuth). */
  apiKey?: boolean;
  now?: number;
};

/** One node's state for a runtime under the hub's rules: null is "unknown" (no entry, or a failed detection pass). */
function holderState(status: NodeRuntimeStatus | undefined, runtime: SeatRuntime, apiKey: boolean): SeatState | null {
  if (!status || status.blocker === "detection_failed") return null;
  return seatState(runtime === "claude-code" && apiKey ? { ...status, ready: status.installed } : status);
}

/**
 * Availability of a runtime for one agent, matching the hub's NodeRunSource.refusal: only nodes holding the agent that were
 * seen within RUNTIME_REPORT_FRESH_MS count; ready on any wins; any holder saying nothing about the runtime (or none holding)
 * is "unknown"; otherwise the least-bad state with its blocker.
 */
export function runtimeAvailability(nodes: NodeDTO[], agentId: string, runtime: SeatRuntime, opts: AvailabilityOptions = {}): Availability {
  const cutoff = (opts.now ?? Date.now()) - RUNTIME_REPORT_FRESH_MS;
  const holders = nodes.filter((n) => n.agentIds.includes(agentId) && n.lastSeenAt >= cutoff);
  const rank: SeatState[] = ["ready", "connect", "needs_keychain", "unsupported", "not_installed"];
  let best: Availability | null = null;
  let unknown = holders.length === 0;
  for (const node of holders) {
    const status = seatStatus(node, runtime);
    const state = holderState(status, runtime, opts.apiKey ?? false);
    if (state === null) { unknown = true; continue; }
    if (state === "ready") return { state, node: node.name, blocker: null };
    if (!best || rank.indexOf(state) < rank.indexOf(best.state)) best = { state, node: node.name, blocker: status?.blocker ?? null };
  }
  if (unknown || !best) return { state: "unknown", node: null, blocker: null };
  return best;
}

/** Whether a runtime may be picked without "set anyway": ready, or no holding node has reported yet (an older node: the hub neither refuses nor offers, §5.1). */
export function isSelectable(av: Availability): boolean {
  return av.state === "ready" || av.state === "unknown";
}

/** The warning kept on an agent card while its saved runtime is not ready on any holding node (§5.3). Null when fine or unknown. */
export function unavailableWarning(nodes: NodeDTO[], agentId: string, runtime: Runtime | null, opts: AvailabilityOptions = {}): string | null {
  if (!isSeatRuntime(runtime)) return null;
  const av = runtimeAvailability(nodes, agentId, runtime, opts);
  if (av.state === "ready" || av.state === "unknown") return null;
  return `${RUNTIME_LABEL[runtime]} is not ready on any node holding this agent. Its tasks wait${av.blocker ? ` (${av.blocker})` : ""}.`;
}

/** New-agent default: the first ready runtime on the creator's own nodes, else claude-code when nothing is ready or reported. */
export function defaultRuntime(nodes: NodeDTO[], memberId: string, now?: number): SeatRuntime {
  // memberId is the agent's owner: the node that will hold it is the owner's. A node silent past the freshness window reports nothing the hub trusts.
  const cutoff = (now ?? Date.now()) - RUNTIME_REPORT_FRESH_MS;
  const mine = nodes.filter((n) => n.memberId === memberId && n.runtimes !== null && n.lastSeenAt >= cutoff);
  for (const runtime of SEAT_RUNTIMES) if (mine.some((n) => seatStatus(n, runtime)?.ready)) return runtime;
  return "claude-code";
}

export type ModelChoices = { defaultModel: string | null; models: string[] };

/** Union of `allowModels` over the holding nodes that report this runtime, plus the first reported default. */
export function modelChoices(nodes: NodeDTO[], agentId: string, runtime: SeatRuntime): ModelChoices {
  const models: string[] = [];
  let defaultModel: string | null = null;
  for (const node of reportingNodes(nodes, agentId)) {
    const status = seatStatus(node, runtime);
    if (!status) continue;
    defaultModel ??= status.defaultModel;
    for (const m of status.allowModels) if (!models.includes(m)) models.push(m);
  }
  return { defaultModel, models };
}

/** Claude aliases that a non-Claude runtime cannot use (G16): the node default is used instead. */
export function isClaudeAlias(model: string): boolean {
  return /^(opus|sonnet|haiku|fable|claude)/i.test(model.trim());
}

export function runtimeCapabilities(runtime: Runtime | null): RunSourceCapabilities {
  return runSourceCapabilities("node", runtime);
}

/** Runtimes an agent can fall back to: the core fallback runtimes except its primary (claude-code joins once core allows it). */
export function fallbackCandidates(primary: Runtime | null): FallbackRuntime[] {
  return FALLBACK_RUNTIMES.filter((r) => r !== primary);
}

/** A fallback order without the primary runtime: the primary can't be its own fallback. */
export function withoutPrimary(order: FallbackRuntime[], primary: Runtime | null): FallbackRuntime[] {
  return order.filter((r) => r !== primary);
}

export function moveItem<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}
